import { useIsFocused, useLocalSearchParams } from 'expo-router';
import { DateTime } from 'luxon';
import {
  type ComponentProps,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { type CellRendererProps, FlatList } from 'react-native';
import Animated, { FadeIn } from 'react-native-reanimated';
import { StyleSheet } from 'react-native-unistyles';
import { useMutation, useQuery } from 'urql';
import ChatInput from '@/components/Chat/ChatInput';
import { ChatMessageItemFragment } from '@/components/Chat/ChatMessageItem.utils';
import { GetChatMessages } from '@/components/Chat/ChatMessages.utils';
import KeyboardAvoidingView from '@/components/Custom/KeyboardAvoidingView';
import Placeholder from '@/components/Custom/Placeholder';
import Box from '@/components/UI/Box';
import { type FragmentOf, graphql, readFragment } from '@/gql/graphql';
import useAppState from '@/hooks/useAppState';
import useUserId from '@/hooks/useUserId';
import { setCursor, useChatStore } from '@/stores/useChatStore';
import isStartOfMessageGroup from '@/utils/isStartOfMessageGroup';
import ChatMessageItem from './ChatMessageItem';
import ChatScrollDownButton from './ChatScrollDownButton';

const INITIAL_MESSAGE_LIMIT = 50;
const ITEMS_TO_SHOW_SCROLL_DOWN_BUTTON = 10;

type ChatMessageListItem = FragmentOf<typeof ChatMessageItemFragment>;
type ChatMessagesViewabilityHandler = NonNullable<
  ComponentProps<typeof FlatList>['onViewableItemsChanged']
>;

const MAINTAIN_VISIBLE_CONTENT_POSITION = {
  autoscrollToTopThreshold: 100,
  minIndexForVisible: 0,
};

const UpdateChatLastSeen = graphql(/* GraphQL */ `
  mutation UpdateChatLastSeen(
    $personId: String!
    $chatId: uuid!
    $lastSeenAt: timestamptz!
  ) {
    insert_chat_person_preference_one(
      object: {
        person_id: $personId
        chat_id: $chatId
        last_seen_at: $lastSeenAt
      }
      on_conflict: {
        constraint: chat_person_preference_pkey
        update_columns: [last_seen_at]
      }
    ) {
      chat_id
      person_id
      last_seen_at
    }
  }
`);

function ChatMessagesCellRenderer({
  children,
  index,
  ...props
}: CellRendererProps<ChatMessageListItem>) {
  return (
    <Box {...props} style={[props.style, style.cellRendererContainer(index)]}>
      {children}
    </Box>
  );
}

function ChatMessages({ unseenMessageCount }: { unseenMessageCount: number }) {
  const listRef = useRef<FlatList<ChatMessageListItem> | null>(null);
  const hoverDisableTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const { chatId: id = '' } = useLocalSearchParams<{
    chatId?: string;
  }>();
  const personId = useUserId();
  const isFocused = useIsFocused();
  const appState = useAppState();
  const canReadChat = isFocused && appState === 'active';
  const [isAtBottom, setIsAtBottom] = useState(false);
  useEffect(() => {
    if (!canReadChat || !isAtBottom) return;
    useChatStore.setState({ viewedChatId: id });
    return () => {
      if (useChatStore.getState().viewedChatId === id) {
        useChatStore.setState({ viewedChatId: null });
      }
    };
  }, [canReadChat, isAtBottom, id]);
  const entryCursor = useChatStore((state) => state.cursor.get(id));
  const [, updateChatLastSeen] = useMutation(UpdateChatLastSeen);
  const isPaginatingRef = useRef(false);
  const [isScrollDownButtonVisible, setIsScrollDownButtonVisible] =
    useState(false);
  const [paginationCursor, setPaginationCursor] = useState<string | null>(null);
  const activeCursor = paginationCursor ?? entryCursor;
  const [{ data: messageData, fetching, stale, error }] = useQuery({
    query: GetChatMessages,
    variables: {
      id,
      limit: INITIAL_MESSAGE_LIMIT,
      cursor: activeCursor!,
    },
    pause: !activeCursor,
  });
  useEffect(() => {
    if (!id) {
      return;
    }
    setPaginationCursor(null);
  }, [id]);
  useEffect(() => {
    if (!entryCursor) {
      setCursor(id, DateTime.local().toISO());
    }
  }, [id, entryCursor]);
  const listData = messageData?.message || [];
  const isNewChat =
    Boolean(activeCursor) &&
    messageData?.message.length === 0 &&
    !fetching &&
    !stale &&
    !error;
  const newestMessageId = readFragment(
    ChatMessageItemFragment,
    listData[0],
  )?.id;
  useEffect(() => {
    if (!fetching) {
      isPaginatingRef.current = false;
    }
  }, [fetching]);
  useEffect(() => {
    if (canReadChat && isAtBottom && newestMessageId) {
      updateChatLastSeen({
        personId,
        chatId: id,
        lastSeenAt: DateTime.local().toISO(),
      });
    }
  }, [
    id,
    canReadChat,
    isAtBottom,
    personId,
    updateChatLastSeen,
    newestMessageId,
  ]);
  useEffect(() => {
    if (process.env.EXPO_OS !== 'web' || typeof document === 'undefined') {
      return;
    }
    const styleId = 'chat-hover-disabled-style';
    let styleElement = document.getElementById(
      styleId,
    ) as HTMLStyleElement | null;
    if (!styleElement) {
      styleElement = document.createElement('style');
      styleElement.id = styleId;
      styleElement.textContent =
        'body.chat-hover-disabled [data-testid="ChatMessageItem"]:hover { background-color: transparent !important; }';
      document.head.appendChild(styleElement);
    }
    return () => {
      styleElement?.parentNode?.removeChild(styleElement);
    };
  }, []);
  useEffect(() => {
    if (
      process.env.EXPO_OS !== 'web' ||
      typeof document === 'undefined' ||
      typeof window === 'undefined'
    ) {
      return;
    }
    let node: HTMLElement | null = null;
    let frame: number | null = null;
    const disableClassName = 'chat-hover-disabled';
    const clearHoverClass = () => {
      document.body?.classList.remove(disableClassName);
      if (hoverDisableTimeoutRef.current) {
        clearTimeout(hoverDisableTimeoutRef.current);
        hoverDisableTimeoutRef.current = null;
      }
    };
    const handleInteraction = () => {
      document.body?.classList.add(disableClassName);
      if (hoverDisableTimeoutRef.current) {
        clearTimeout(hoverDisableTimeoutRef.current);
      }
      hoverDisableTimeoutRef.current = setTimeout(() => {
        document.body?.classList.remove(disableClassName);
        hoverDisableTimeoutRef.current = null;
        window.dispatchEvent(new CustomEvent('chat-hover-enabled'));
      }, 150);
    };
    const attachListeners = () => {
      const target =
        listRef.current?.getScrollableNode?.() ??
        listRef.current?.getNativeScrollRef?.() ??
        listRef.current;
      if (
        target &&
        typeof (target as HTMLElement).addEventListener === 'function'
      ) {
        node = target as HTMLElement;
        node.addEventListener('scroll', handleInteraction, {
          passive: true,
        });
        node.addEventListener('wheel', handleInteraction, {
          passive: true,
        });
        node.addEventListener('touchmove', handleInteraction, {
          passive: true,
        });
        return;
      }
      frame = window.requestAnimationFrame(attachListeners);
    };
    attachListeners();
    return () => {
      if (frame) {
        window.cancelAnimationFrame(frame);
      }
      if (node) {
        node.removeEventListener('scroll', handleInteraction);
        node.removeEventListener('wheel', handleInteraction);
        node.removeEventListener('touchmove', handleInteraction);
      }
      clearHoverClass();
    };
  }, []);
  const loadMoreMessages = () => {
    if (
      fetching ||
      isPaginatingRef.current ||
      listData.length < INITIAL_MESSAGE_LIMIT
    ) {
      return;
    }
    const oldestMessage = listData[listData.length - 1];
    const nextCursor = oldestMessage
      ? readFragment(ChatMessageItemFragment, oldestMessage).created_at
      : null;
    if (nextCursor) {
      isPaginatingRef.current = true;
      setPaginationCursor(nextCursor);
    }
  };
  const renderItem = ({
    item,
    index,
  }: {
    item: FragmentOf<typeof ChatMessageItemFragment>;
    index: number;
  }) => {
    const message = readFragment(ChatMessageItemFragment, item);
    const previousItemObj = readFragment(
      ChatMessageItemFragment,
      listData[index + 1],
    );
    const previousItem = previousItemObj
      ? {
          creatorId: previousItemObj?.creator?.id,
          createdAt: previousItemObj?.created_at,
          hasMedia: !!previousItemObj?.media.length,
        }
      : undefined;
    const currentItem = {
      creatorId: message.creator?.id,
      createdAt: message.created_at,
      hasMedia: !!message.media.length,
    };
    return (
      <ChatMessageItem
        fragment={item}
        isGroup={isStartOfMessageGroup(currentItem, previousItem)}
        isBeingEdited={false}
        index={index}
      />
    );
  };
  const onViewableItemsChanged = useCallback<ChatMessagesViewabilityHandler>(
    (info) => {
      const lastVisibleItem = info.viewableItems[0];
      if (!lastVisibleItem) {
        return;
      }
      const relativeIndex = lastVisibleItem.index ?? 0;
      setIsScrollDownButtonVisible((prev) => {
        if (!prev && relativeIndex > ITEMS_TO_SHOW_SCROLL_DOWN_BUTTON) {
          return true;
        } else if (prev && relativeIndex <= ITEMS_TO_SHOW_SCROLL_DOWN_BUTTON) {
          return false;
        }
        return prev;
      });
      const isAtBottom = lastVisibleItem.index === 0;
      setIsAtBottom(isAtBottom);
    },
    [],
  );

  return (
    <KeyboardAvoidingView>
      {isNewChat ? (
        <Animated.View entering={FadeIn} style={style.placeholderContainer}>
          <Placeholder
            text="It's the beginning of a new chat. Say hi!"
            icon="chatbubble-outline"
          />
        </Animated.View>
      ) : null}

      <Box flex overflow="hidden">
        <FlatList
          inverted
          CellRendererComponent={ChatMessagesCellRenderer}
          keyExtractor={(item) =>
            readFragment(ChatMessageItemFragment, item).id
          }
          ref={listRef}
          onEndReached={loadMoreMessages}
          onStartReached={() => setIsAtBottom(true)}
          style={style.list}
          onEndReachedThreshold={0.1}
          onStartReachedThreshold={0.1}
          contentContainerStyle={style.listContentContainer}
          keyboardShouldPersistTaps="always"
          initialNumToRender={INITIAL_MESSAGE_LIMIT}
          keyboardDismissMode="interactive"
          data={listData}
          renderItem={renderItem}
          maintainVisibleContentPosition={MAINTAIN_VISIBLE_CONTENT_POSITION}
          onViewableItemsChanged={onViewableItemsChanged}
        />

        {isScrollDownButtonVisible ? (
          <ChatScrollDownButton
            listRef={listRef}
            unseenMessageCount={unseenMessageCount}
          />
        ) : null}
      </Box>
      <ChatInput listRef={listRef} messages={listData} />
    </KeyboardAvoidingView>
  );
}
const style = StyleSheet.create((theme) => ({
  cellRendererContainer: (index: number) => ({
    zIndex: process.env.EXPO_OS === 'web' ? -index : undefined,
  }),
  list: {
    flex: 1,
  },
  listContentContainer: {
    paddingVertical: theme.space('xl'),
  },
  placeholderContainer: {
    left: 0,
    right: 0,
    top: 0,
    bottom: 0,
    justifyContent: 'center',
    alignItems: 'center',
    position: 'absolute',
  },
}));
export default ChatMessages;
